import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, AlertCircle, Copy, Check, Globe, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLocale } from '@/hooks/useLocale';
import { toast } from 'sonner';
import { isLiveHost } from '@/lib/isLiveHost';

interface AuthGuardProps {
  children: React.ReactNode;
}

function AuthGuard({ children }: AuthGuardProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isApproved, setIsApproved] = useState<boolean | null>(null);
  const [profileCreatedAt, setProfileCreatedAt] = useState<string | null>(null);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [copiedRef, setCopiedRef] = useState(false);
  const [bootError, setBootError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useLocale();

  // Use refs to avoid re-running the effect when navigate/fetchApproval change
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Cache approval per userId to avoid refetching on every auth event
  const approvalCacheRef = useRef<Record<string, { approved: boolean; createdAt: string | null; referralCode: string | null }>>({});

  const fetchApproval = useCallback(async (userId: string) => {
    // Return cached value if we already fetched for this user
    if (userId in approvalCacheRef.current) {
      return approvalCacheRef.current[userId];
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('is_approved, created_at, referral_code')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('[AuthGuard] Failed to fetch approval:', error);
      throw new Error(error.message);
    }

    const approved = data?.is_approved ?? false;
    const createdAt = data?.created_at ?? null;
    const referralCode = (data as any)?.referral_code ?? null;
    const result = { approved, createdAt, referralCode };
    approvalCacheRef.current[userId] = result;
    return result;
  }, []);

  useEffect(() => {
    let mounted = true;
    // Track the current userId so the ban-check interval can invalidate the cache.
    let currentUserId: string | null = null;

    const applySession = (session: Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session'], source: string) => {
      if (!mounted) return;

      console.debug(`[AuthGuard] applySession called — source: ${source}, hasSession: ${!!session}`);

      if (!session) {
        console.debug('[AuthGuard] No session — redirecting to /auth');
        setIsAuthenticated(false);
        setIsApproved(null);
        setProfileCreatedAt(null);
        setIsLoading(false);
        // On live hosts, route unauthenticated visitors to the email-capture
        // waitlist instead of the normal auth page.
        if (isLiveHost()) {
          const sourcePath = window.location.pathname + window.location.search;
          navigateRef.current(`/waitlist?source=${encodeURIComponent(sourcePath)}`, { replace: true });
          return;
        }
        // Preserve the ?ref= param so referral codes aren't dropped on redirect
        const refParam = new URLSearchParams(window.location.search).get('ref');
        const authPath = refParam ? `/auth?ref=${encodeURIComponent(refParam)}` : '/auth';
        navigateRef.current(authPath, { replace: true });
        return;
      }

      // Track current user for the ban-check interval.
      currentUserId = session.user.id;

      // If we already have a cached approval for this user, skip the loading flash
      const cachedApproval = approvalCacheRef.current[session.user.id];
      if (cachedApproval !== undefined) {
        console.debug(`[AuthGuard] Using cached approval for ${session.user.id}: ${cachedApproval.approved}`);
        setIsAuthenticated(true);
        setIsApproved(cachedApproval.approved);
        setProfileCreatedAt(cachedApproval.createdAt);
        setReferralCode(cachedApproval.referralCode);
        setIsLoading(false);
        return;
      }

      setIsAuthenticated(true);
      setIsLoading(true);
      setBootError(null);

      // Race the approval check against a timeout
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out')), 10000)
      );

      Promise.race([fetchApproval(session.user.id), timeout])
        .then((result) => {
          if (!mounted) return;
          setIsApproved(result.approved);
          setProfileCreatedAt(result.createdAt);
          setReferralCode(result.referralCode);
          setIsLoading(false);
        })
        .catch((err) => {
          if (!mounted) return;
          console.error('[AuthGuard] Boot error:', err);
          setBootError(err?.message || 'Failed to load account data');
          setIsLoading(false);
        });
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      applySession(session, 'initial-getSession');
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.debug(`[AuthGuard] onAuthStateChange event: ${event}`);
      // React to sign-in/sign-out. Also react to TOKEN_REFRESHED with a null
      // session — that means the server-side session was revoked (e.g. admin ban).
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        if (!session) currentUserId = null;
        window.setTimeout(() => applySession(session, `authChange-${event}`), 0);
      } else if (event === 'TOKEN_REFRESHED' && !session) {
        currentUserId = null;
        window.setTimeout(() => applySession(null, 'authChange-TOKEN_REFRESHED-null'), 0);
      }
    });

    // Re-check approval when the user switches back to this tab.
    // This ensures a user waiting on the approval screen is unblocked promptly
    // once an admin grants access — without requiring a sign-out / sign-in cycle.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (!session?.user?.id) return;
          // Invalidate the cached approval for this user so applySession re-fetches.
          delete approvalCacheRef.current[session.user.id];
          applySession(session, 'visibility-refocus');
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Periodically attempt a token refresh to detect server-side session revocation
    // (e.g. admin ban). Supabase fires SIGNED_OUT when the refresh fails, which is
    // caught by onAuthStateChange above. Without this check, a valid JWT can keep
    // the session alive for up to 1 hour after an admin ban. (Audit #27)
    const banCheckInterval = window.setInterval(async () => {
      if (!currentUserId) return; // not signed in — nothing to check
      const { error } = await supabase.auth.refreshSession();
      if (error) {
        console.warn('[AuthGuard] Periodic refresh failed — session likely revoked:', error.message);
        // Invalidate cache so the next applySession re-fetches from DB.
        if (currentUserId) delete approvalCacheRef.current[currentUserId];
        applySession(null, 'periodic-ban-check');
      }
    }, 5 * 60 * 1000); // every 5 minutes

    return () => {
      mounted = false;
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.clearInterval(banCheckInterval);
    };
  }, [fetchApproval]); // navigate removed — using ref instead

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (bootError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center space-y-6 max-w-md">
          <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground">Something went wrong</h1>
            <p className="text-muted-foreground">
              We couldn't load your account data. This may be a temporary network issue.
            </p>
            <p className="text-xs text-muted-foreground font-mono bg-muted rounded p-2 mt-2">
              {bootError}
            </p>
          </div>
          <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={() => window.location.reload()}>
              Retry
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                await supabase.auth.signOut();
                navigateRef.current('/auth', { replace: true });
              }}
            >
              {t.logout}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isAuthenticated && isApproved === false) {
    const supportEmail = 'support@adventureventure.app';
    const referralUrl = referralCode
      ? `https://adventureventure.app/?ref=${referralCode}`
      : null;

    let daysWaiting: number | null = null;
    if (profileCreatedAt) {
      const createdMs = new Date(profileCreatedAt).getTime();
      if (!Number.isNaN(createdMs)) {
        daysWaiting = Math.max(0, Math.floor((Date.now() - createdMs) / (1000 * 60 * 60 * 24)));
      }
    }

    const handleCopyRef = () => {
      if (!referralUrl) return;
      navigator.clipboard.writeText(referralUrl).then(() => {
        setCopiedRef(true);
        toast.success('Referral link copied!');
        setTimeout(() => setCopiedRef(false), 2000);
      });
    };

    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4 py-16">
        <div className="w-full max-w-md space-y-8">

          {/* Header */}
          <div className="text-center space-y-3">
            <div className="text-5xl mb-2 select-none">🎲</div>
            <h1 className="text-3xl font-black text-foreground tracking-tight">
              You're on the list.
            </h1>
            <p className="text-muted-foreground leading-relaxed">
              We're reviewing your application and will email you when your adventure begins.
              {daysWaiting !== null && daysWaiting > 0 && (
                <span className="block text-sm mt-1 text-muted-foreground/70">
                  Applied {daysWaiting} {daysWaiting === 1 ? 'day' : 'days'} ago.
                </span>
              )}
            </p>
          </div>

          {/* Referral link */}
          {referralUrl && (
            <div className="rounded-xl border border-border bg-card p-5 space-y-3">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground">
                  Invite friends — move up the list
                </p>
                <p className="text-xs text-muted-foreground">
                  Share your link. Every friend who applies with it helps your application get prioritised.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={referralUrl}
                  className="flex-1 min-w-0 text-xs bg-muted border border-border rounded-lg px-3 py-2 text-muted-foreground font-mono truncate"
                />
                <button
                  onClick={handleCopyRef}
                  className="shrink-0 flex items-center gap-1.5 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 transition-colors"
                >
                  {copiedRef ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copiedRef ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          {/* While you wait */}
          <div className="space-y-3">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/50 font-semibold text-center">
              While you wait
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => navigateRef.current('/explore')}
                className="p-4 rounded-xl bg-card border border-border hover:border-primary/40 transition-all text-left group"
              >
                <Globe className="w-5 h-5 text-primary mb-2" />
                <div className="text-sm font-semibold text-foreground">Browse Worlds</div>
                <div className="text-xs text-muted-foreground mt-0.5">See what's possible</div>
              </button>
              <button
                onClick={() => navigateRef.current('/about')}
                className="p-4 rounded-xl bg-card border border-border hover:border-primary/40 transition-all text-left group"
              >
                <BookOpen className="w-5 h-5 text-primary mb-2" />
                <div className="text-sm font-semibold text-foreground">Read the Vision</div>
                <div className="text-xs text-muted-foreground mt-0.5">What the AI can do</div>
              </button>
            </div>
          </div>

          {/* Footer actions */}
          <div className="flex flex-col items-center gap-2 pt-2">
            <p className="text-xs text-muted-foreground/60">
              Questions?{' '}
              <a
                href={`mailto:${supportEmail}`}
                className="text-primary hover:underline"
              >
                {supportEmail}
              </a>
            </p>
            <button
              onClick={async () => {
                await supabase.auth.signOut();
                navigateRef.current('/auth', { replace: true });
              }}
              className="text-xs text-muted-foreground/40 hover:text-muted-foreground transition-colors"
            >
              Sign out
            </button>
          </div>

        </div>
      </div>
    );
  }

  if (isAuthenticated && isApproved) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="text-center space-y-4">
        <p className="text-muted-foreground">Session not found. Redirecting…</p>
        <Button variant="outline" onClick={() => navigateRef.current('/auth', { replace: true })}>
          Go to Sign In
        </Button>
      </div>
    </div>
  );
}

export default AuthGuard;
